import { CalendarDays, Clock, Users } from "lucide-react";
import Modal from "./Modal";
import { BLOCK_HOURS, countBlocks } from "../utils/bookingBlocks";

const formatWindowPart = (value) =>
  value.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

// Mirrors config.booking.cancellationCutoffHours on the server: how long
// before the trip starts the right to cancel closes.
const CANCELLATION_CUTOFF_HOURS = 12;
// Pulo's commission, earned when the booking is paid for and never refunded.
const COMMISSION_PERCENT = 20;

const formatMoney = (amount, symbol = "₦") =>
  `${symbol}${(Number(amount) || 0).toLocaleString("en-NG")}`;

const formatTotal = (amount, symbol = "₦") =>
  `${symbol}${(Number(amount) || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Step 2 of booking: an itemised review (base assistant fee + each selected
 * escort vehicle and security personnel) with the grand total, before choosing
 * a payment method. Payment lives in PaymentMethodModal.
 */
function AssistantBookingReviewModal({
  isOpen,
  onClose,
  onBack,
  onProceed,
  bookingDetails,
  assistantName = "Pulo Assistant",
  baseAmount = 0,
  totalAmount = 0,
  currencySymbol = "₦",
}) {
  const vehicleItems = (bookingDetails?.escortVehicles || []).map((item) => ({
    ...item,
    type: "vehicle",
  }));
  const personnelItems = (bookingDetails?.securityPersonnel || []).map(
    (item) => ({ ...item, type: "personnel" })
  );

  // Bookings are a window now, not just a pair of dates — show the exact
  // hours being reserved before the user pays for them.
  const arrival = bookingDetails?.arrivalDate
    ? new Date(bookingDetails.arrivalDate)
    : null;
  const departure = bookingDetails?.departureDate
    ? new Date(bookingDetails.departureDate)
    : null;
  const bookingWindow =
    arrival && departure
      ? `${formatWindowPart(arrival)} → ${formatWindowPart(departure)}`
      : "";
  const blocks =
    Number(bookingDetails?.blocks) ||
    (arrival && departure ? countBlocks(arrival, departure) : 0);
  const blockSummary = blocks
    ? `${blocks} × ${BLOCK_HOURS}hrs (${blocks * BLOCK_HOURS} hours total)`
    : "";

  const lineItems = [
    {
      key: "base",
      label: assistantName || "Pulo Assistant",
      amount: Number(baseAmount) || 0,
      type: "base",
    },
    ...vehicleItems.map((item) => ({
      key: item.key,
      label: item.label,
      amount: item.subtotal,
      type: "vehicle",
    })),
    ...personnelItems.map((item) => ({
      key: item.key,
      label: item.label,
      amount: item.subtotal,
      type: "personnel",
    })),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} panelClassName="max-w-lg">
      <div className="space-y-6">
        <div className="space-y-2 pr-10">
          <h3 className="text-lg font-bold text-[#570033]">
            Review Your Booking
          </h3>
          <p className="text-sm text-slate-500">
            Take a moment to review the details of your booking before
            proceeding to payment.
          </p>
        </div>

        {bookingWindow ? (
          <div className="space-y-1 rounded-xl border border-[#E7C0D6] bg-[#FBF5F9] px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#570033]">
              <Clock className="h-4 w-4" />
              Booked window
            </div>
            <p className="text-sm font-semibold text-slate-700">{bookingWindow}</p>
            <p className="text-xs text-slate-500">{blockSummary}</p>
          </div>
        ) : null}

        <div className="space-y-4">
          <h4 className="text-base font-bold text-slate-900">Booking Details</h4>
          <div className="space-y-4">
            {lineItems.map((item) => {
              const Icon = item.type === "personnel" ? Users : CalendarDays;
              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <div className="flex items-center gap-3 text-slate-600">
                    <Icon className="h-5 w-5 text-slate-400" />
                    <span>{item.label}</span>
                  </div>
                  <span className="font-bold text-[#570033]">
                    {formatMoney(item.amount, currencySymbol)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {bookingDetails?.itinerary ? (
          <div className="space-y-2 rounded-xl border border-slate-200 px-4 py-3">
            <h4 className="text-sm font-bold text-slate-900">Itinerary</h4>
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">
              {bookingDetails.itinerary}
            </p>
          </div>
        ) : null}

        <div className="rounded-2xl bg-slate-100 px-4 py-5 text-center">
          <span className="text-lg font-bold text-[#570033]">
            Total:&nbsp; {formatTotal(totalAmount, currencySymbol)}
          </span>
        </div>

        {/* The refund rule decides whether money comes back, so it belongs on
            the last screen before payment rather than in a policy page. */}
        <div className="space-y-1 rounded-xl border border-slate-200 bg-[#FFF9F2] px-4 py-3">
          <h4 className="text-sm font-bold text-slate-900">
            Cancellation policy
          </h4>
          <p className="text-xs leading-5 text-slate-600">
            Cancel up to {CANCELLATION_CUTOFF_HOURS} hours before your trip
            starts for a full refund, minus Pulo's {COMMISSION_PERCENT}%
            commission. No cancellations after that.
          </p>
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={onProceed}
            className="w-full rounded-xl bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#450029]"
          >
            Proceed to Payment
          </button>
          <button
            type="button"
            onClick={onBack}
            className="w-full rounded-xl border border-[#570033] py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#F8EEF4]"
          >
            Go back
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default AssistantBookingReviewModal;
